// components/Features.jsx
import React, { useEffect, useState } from "react";
import "./Features.css";

import HeadsetMicRoundedIcon from "@mui/icons-material/HeadsetMicRounded";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import FlightTakeoffRoundedIcon from "@mui/icons-material/FlightTakeoffRounded";
import LocalTaxiRoundedIcon from "@mui/icons-material/LocalTaxiRounded";
import HotelRoundedIcon from "@mui/icons-material/HotelRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";

const featureCards = [
  {
    id: "voice",
    title: "Voice companion in your language",
    emoji: "🎧",
    Icon: HeadsetMicRoundedIcon,
    description:
      "Gentle, voice-led support that speaks to you in words you understand.",
  },
  {
    id: "planning",
    title: "Pre-travel planning made simple",
    emoji: "🧳",
    Icon: ChecklistRoundedIcon,
    description:
      "Checklists, tips, and essentials to prepare with confidence before you go.",
  },
  {
    id: "rituals",
    title: "Step-by-step ritual guidance",
    emoji: "📖",
    Icon: MenuBookRoundedIcon,
    description:
      "Clear instructions for every Hajj & Umrah ritual, from start to finish.",
  },
  {
    id: "navigation",
    title: "Navigation so you never get lost",
    emoji: "🧭",
    Icon: MapRoundedIcon,
    description:
      "Simple maps to find gates, hotels, rituals, and meeting points in the crowd.",
  },
  {
    id: "group",
    title: "Group & family stay connected",
    emoji: "👨‍👩‍👧‍👦",
    Icon: GroupsRoundedIcon,
    description:
      "Share locations, set meeting points, and quickly reconnect if someone drifts away.",
  },
  {
    id: "reminders",
    title: "Smart reminders & safety tools",
    emoji: "⚠️",
    Icon: NotificationsActiveRoundedIcon,
    description:
      "Timely nudges and quick access to key info and emergency contacts.",
  }
];

function Features() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(3);

  // responsive cards per view (for MOBILE/TABLET carousel only)
  useEffect(() => {
    const updateCardsPerView = () => {
      const w = window.innerWidth;
      if (w < 640) setCardsPerView(1);
      else if (w < 1024) setCardsPerView(2);
      else setCardsPerView(3);
    };

    updateCardsPerView();
    window.addEventListener("resize", updateCardsPerView);
    return () => window.removeEventListener("resize", updateCardsPerView);
  }, []);

  const maxIndex = Math.max(0, featureCards.length - cardsPerView);

  useEffect(() => {
    if (activeIndex > maxIndex) {
      setActiveIndex(maxIndex);
    }
  }, [cardsPerView, maxIndex, activeIndex]);

  // auto-swipe (mobile / tablet)
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 4500);
    return () => clearInterval(timer);
  }, [maxIndex, cardsPerView]);

  const goNext = () =>
    setActiveIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  const goPrev = () =>
    setActiveIndex((prev) => (prev === 0 ? maxIndex : prev - 1));

  const slideWidth = 100 / cardsPerView;

  const renderCard = ({ id, title, emoji, Icon, description }) => (
    <article className="feature-card" key={id}>
      <div className="feature-card-top">
        <span className="feature-emoji" aria-hidden="true">
          {emoji}
        </span>
        <span className="feature-icon">
          <Icon fontSize="small" />
        </span>
      </div>
      <h3 className="feature-title">{title}</h3>
      <p className="feature-body">{description}</p>
      
    </article>
  );

  return (
    <section className="support-section" id="features">
      <div className="support-inner">
        <div className="support-header">
          <h2 className="support-title">
            Support You Can Feel, Wherever You Are
          </h2>
          <p className="support-subtitle">
            Whether you&apos;re still packing at home or moving through the
            crowds in Makkah and Madinah, Tawaf supports you with clear
            guidance, so you never feel alone in the journey. Tawaf brings
            together the essentials pilgrims need, in one simple app, without
            overwhelming menus or complicated screens.
          </p>
        </div>

        {/* 🖥️ Desktop: 3x3 grid of all features */}
        <div className="support-grid-desktop">
          {featureCards.map((card) => (
            <div className="support-grid-item" key={card.id}>
              {renderCard(card)}
            </div>
          ))}
        </div>

        {/* 📱 Mobile / Tablet: Carousel slider */}
        <div className="support-slider-shell">
          <div className="support-slider">
            <div
              className="support-track"
              style={{
                transform: `translateX(-${activeIndex * slideWidth}%)`,
              }}
            >
              {featureCards.map((card) => (
                <div
                  className="support-slide"
                  key={card.id}
                  style={{ flex: `0 0 ${slideWidth}%` }}
                >
                  {renderCard(card)}
                </div>
              ))}
            </div>
          </div>

          <div className="support-slider-controls">
            <button
              type="button"
              className="support-arrow"
              aria-label="Previous feature"
              onClick={goPrev}
            >
              <KeyboardArrowLeftRoundedIcon fontSize="small" />
            </button>
            <div className="support-dots">
              {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`support-dot ${
                    i === activeIndex ? "support-dot--active" : ""
                  }`}
                  onClick={() => setActiveIndex(i)}
                  aria-label={`Go to feature set ${i + 1}`}
                />
              ))}
            </div>
            <button
              type="button"
              className="support-arrow"
              aria-label="Next feature"
              onClick={goNext}
            >
              <KeyboardArrowRightRoundedIcon fontSize="small" />
            </button>
          </div>

          <p className="support-note">
            ✨ Smarter bookings for your journey – Tawaf helps you discover
            flights, local transport, and places to stay that fit your schedule,
            budget, and comfort.
          </p>
        </div>
      </div>
    </section>
  );
}

export default Features;
